import clsx from 'clsx';
import Link from 'next/link';
import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { VariantGroup, VariantOption } from '~/features/product-details/types';
import { Typography } from '~/shared/components';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import Spinner from '~/shared/components/Spinner/Spinner';
import { useCanHover } from '~/shared/hooks/useCanHover';
import styles from './ProductVariantSelector.module.scss';

enum InputType {
    THUMBS = 'thumbnails',
    BUTTONS = 'buttons',
}

const THUMBNAIL_ATTRIBUTE_IDS = ['supplierColour', 'color'];
const COMPONENT_LIMIT = 5;
const PREVIEW_CLEAR_DELAY_MS = 150;

const getInputType = (attributeId: string) =>
    THUMBNAIL_ATTRIBUTE_IDS.includes(attributeId) ? InputType.THUMBS : InputType.BUTTONS;

const isOptionDisabled = (option: VariantOption) =>
    !option.isOrderable || option.matchQuality === 'none';

const getNumericValue = (option: VariantOption): number =>
    parseFloat(option.value.replace(',', '.').split(' ')[0]);

const byValue = (a: VariantOption, b: VariantOption): number => {
    const aNumber = getNumericValue(a);
    const bNumber = getNumericValue(b);

    // We will sort by numbers if available.
    if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) return aNumber - bNumber;

    // Else, we will sort alphabetically.
    return a.value.localeCompare(b.value);
};

/**
 * Implements optional debounced preview labels for our options. The debounce is for
 * the gap between the options to prevent flickers.
 */
const useOptionPreview = (isEnabled: boolean) => {
    const [previewLabel, setPreviewLabel] = useState<string>();
    const clearTimer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(clearTimer.current), []);

    const preview = (label?: string) => {
        clearTimeout(clearTimer.current);

        if (label !== undefined) {
            setPreviewLabel(label);

            return;
        }

        clearTimer.current = setTimeout(() => setPreviewLabel(undefined), PREVIEW_CLEAR_DELAY_MS);
    };

    return { previewLabel, onPreview: isEnabled ? preview : undefined };
};

interface VariantProps {
    option: VariantOption;
    isHidden?: boolean;
    /** Fresh product data is on its way, so no option can be picked until it lands. */
    isLoading?: boolean;
    onPreview?(label?: string): void;
    selectedProductId?: string;
    onClick(event: React.MouseEvent<HTMLElement>): void;
}

const VariantLink: React.FC<PropsWithChildren<VariantProps & { className: string }>> = ({
    className,
    option,
    isHidden,
    isLoading,
    selectedProductId,
    onPreview,
    onClick,
    children,
}) => {
    /**
     * The selection leads the data: `selectedProductId` is the product being switched to,
     * while `option.isSelected` still describes the one on screen. Matching on the product id
     * rather than the value also holds for the variants that share a master's url.
     */
    const isBusy = !!isLoading && !!option.productId && option.productId === selectedProductId;

    const sharedProps = {
        className: clsx(className, {
            [styles.isSelected]: option.isSelected,
            [styles.isDisabled]: isOptionDisabled(option),
            [styles.isHidden]: isHidden,
            [styles.isLoading]: isLoading,
            [styles.isBusy]: isBusy,
        }),
        'aria-disabled': isLoading || isOptionDisabled(option) || undefined,
        'aria-busy': isBusy || undefined,
        onMouseEnter: onPreview && (() => onPreview(option.label)),
        onMouseLeave: onPreview && (() => onPreview()),
        onFocus: onPreview && (() => onPreview(option.label)),
        onBlur: onPreview && (() => onPreview()),
    };

    const content = (
        <>
            {children}

            {isBusy && (
                <span className={styles.variantSpinner}>
                    <Spinner />
                </span>
            )}
        </>
    );

    // A link would still navigate on click, so while loading it renders as inert markup.
    if (!option.href || isLoading) return <div {...sharedProps}>{content}</div>;

    return (
        <Link {...sharedProps} href={option.href} onClick={onClick}>
            {content}
        </Link>
    );
};

const VariantButton: React.FC<VariantProps> = (props) => (
    <VariantLink className={styles.variantButton} {...props}>
        <Typography variant="paragraph-S">{props.option.label}</Typography>
    </VariantLink>
);

const VariantThumb: React.FC<VariantProps> = (props) => {
    const { option } = props;

    return (
        <VariantLink className={styles.variantThumb} {...props}>
            <span className={styles.srOnly}>{option.label}</span>

            {option.imageUrl && (
                <DynamicImageLoader
                    className={styles.variantThumbPreview}
                    src={option.imageUrl}
                    alt={option.label}
                    width={48}
                    height={48}
                />
            )}

            {isOptionDisabled(option) && (
                <div className={styles.variantThumbDisabled}>
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g clipPath="url(#clip0_1_2)">
                            <rect width="48" height="48" fill="none" />

                            <line
                                x1="-1.35726"
                                y1="47.6502"
                                x2="45.6427"
                                y2="-0.349813"
                                stroke="#8E8E8D"
                            />

                            <line
                                x1="-1.64296"
                                y1="-2.35004"
                                x2="48.357"
                                y2="48.65"
                                stroke="#8E8E8D"
                            />
                        </g>

                        <defs>
                            <clipPath id="clip0_1_2">
                                <rect width="48" height="48" fill="none" />
                            </clipPath>
                        </defs>
                    </svg>
                </div>
            )}
        </VariantLink>
    );
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    group: VariantGroup;
    isLoading?: boolean;
    selectedProductId?: string;
    onVariantClick(option: VariantOption, event: React.MouseEvent<HTMLElement>): void;
}

export const ProductVariantSelector: React.FC<Props> = ({
    group,
    className,
    isLoading,
    selectedProductId,
    onVariantClick,
    ...rest
}) => {
    const inputType = getInputType(group.id);

    const Component = inputType === InputType.THUMBS ? VariantThumb : VariantButton;

    const options = useMemo(() => {
        // We will render all variant thumbs by default.
        if (inputType === InputType.THUMBS) return group.options;

        // For the rest, we will not render options without product IDs and sort them by value.
        return group.options.filter((option) => !!option.productId).sort(byValue);
    }, [group.options, inputType]);

    const canHover = useCanHover();

    // If hoverable or tabbable, we will enable label previews on thumbs.
    const { previewLabel, onPreview } = useOptionPreview(
        canHover && inputType === InputType.THUMBS,
    );

    // Collapsing is currently exclusive to InputType.THUMBS.
    const [shouldShowAll, setShouldShowAll] = useState(inputType === InputType.BUTTONS);
    const optionsRef = useRef<HTMLDivElement>(null);
    const hasJustRevealed = useRef(false);

    /**
     * The button removes itself once everything is shown, so focus would otherwise fall back to
     * the document. It moves to the first option that was behind it - counting from the limit,
     * and skipping any that render as a plain element because they are unavailable.
     */
    useEffect(() => {
        if (!hasJustRevealed.current) return;

        hasJustRevealed.current = false;

        const options = optionsRef.current?.querySelectorAll<HTMLElement>(
            `.${styles.variantThumb}, .${styles.variantButton}`,
        );

        const nextOption = Array.from(options ?? [])
            .slice(COMPONENT_LIMIT)
            .find((option) => option.matches('a[href]'));

        nextOption?.focus();
    }, [shouldShowAll]);

    const isOptionHidden = (index: number) => !shouldShowAll && index >= COMPONENT_LIMIT;

    const hiddenItemsTotal = shouldShowAll ? 0 : Math.max(options.length - COMPONENT_LIMIT, 0);

    return (
        <div
            ref={optionsRef}
            className={clsx(styles.variantSelector, className)}
            data-input-type={inputType}
            {...rest}
        >
            <Typography className={styles.variantValue} variant="paragraph-S" as="p">
                <span>{previewLabel ?? group.selectedValue}</span>
            </Typography>

            {options.map((option, index) => (
                <Component
                    key={`${group.id}-${option.value}`}
                    option={option}
                    isHidden={isOptionHidden(index)}
                    isLoading={isLoading}
                    selectedProductId={selectedProductId}
                    onPreview={onPreview}
                    onClick={(event) => onVariantClick(option, event)}
                />
            ))}

            {hiddenItemsTotal > 0 && (
                <button
                    className={styles.toggleMoreThumbs}
                    onClick={() => {
                        hasJustRevealed.current = true;
                        setShouldShowAll(true);
                    }}
                    aria-label={`Vis ${hiddenItemsTotal} flere elementer`}
                >
                    <span>+{hiddenItemsTotal}</span>
                </button>
            )}
        </div>
    );
};
